import React, { createContext, useEffect, useReducer } from "react"
import { Helmet } from "react-helmet"
import languageReducer from './languageReducer'
import cookie from 'react-cookies'

import fr from '../../utils/i18n/fr.json'
import en from '../../utils/i18n/en.json'
import nl from '../../utils/i18n/nl.json'

import { SET_CURRENT_LOCALE } from "../types"

// const WECAR_LANGUAGE_COOKIE = "wecar_lang"
const WECAR_LOCALE_COOKIE = "wecar_locale"

export const LanguageContext = createContext()

const source = {
  fr,
  en,
  nl,
}

const DEFAULT_LANGUAGE = "fr"
const DEFAULT_LOCALE = "fr-FR"

export const LOCALE_OPTIONS = [
  { value: 'fr-FR', label: 'Français', default: true }, // Default locale if no cookie is set and browser locale is French
  { value: 'nl-NL', label: 'Nederlands', default: true }, // Default locale if no cookie is set and browser locale is Dutch
  { value: 'en-BE', label: 'English', default: true }, // Default locale if no cookie is set and browser locale is English
  { value: 'en-GB', label: 'English (UK)', default: false }, // English UK locale (set if user location is UK)
]

const LanguageState = ({ children }) => {

  const initialState = {
    currentLanguage: DEFAULT_LANGUAGE, // cookie.load(WECAR_LANGUAGE_COOKIE) || DEFAULT_LANGUAGE
    currentLocale: DEFAULT_LOCALE, // cookie.load(WECAR_LOCALE_COOKIE) || DEFAULT_LOCALE
  }

  const [state, dispatch] = useReducer(languageReducer, initialState)
  
  const extractArrayOfKeys = (sectionKey, keyPrefix, forcedLanguage) => {
    const language = forcedLanguage || state.currentLanguage
    const section = source[language][sectionKey]
    return Object.keys(section)?.filter(key => key.startsWith(keyPrefix))
  }

  useEffect(() => {

    const detectUKUserLocale = async () => {
      try {
        const response = await fetch('https://api.ipgeolocation.io/ipgeo?apiKey=ee42076b94e3495ea0aebb4bf9e06a04')
        const data = await response.json()
        if (data.country_code2.toLowerCase() === 'gb') {
          setLocale('en-GB')
          return true
        }
        return false
      } catch (error) {
        console.error('Error fetching user location:', error)
        return false
      }
    }
    const initializeLocale = async () => {
      const cookieLocale = cookie.load(WECAR_LOCALE_COOKIE)
      const validLocales = LOCALE_OPTIONS.map(option => option.value)

      const validLanguages = validLocales.map(locale => locale.split('-')[0])
      if (cookieLocale && validLocales.includes(cookieLocale)) {
        // setting locale from cookie if it is valid
        setLocale(cookieLocale)
      } else {
        // If no cookie is set or is invalid, check IP or browser locale
        const localeSet = await detectUKUserLocale()
        if (localeSet) return // If user is in the UK, locale is already set to en-GB

        const browserLocale = window.navigator.language

        if (validLocales.includes(browserLocale)) {
          // setting locale from full browser locale if it is valid
          setLocale(browserLocale)
        } else {
          const browserLanguage = browserLocale.split('-')[0]
          if (validLanguages.includes(browserLanguage)) {
            // setting locale from browser language if it is valid
            setLocale(LOCALE_OPTIONS.find(locale => locale.value.startsWith(browserLanguage) && locale.default)?.value || DEFAULT_LOCALE)
          } else {
            // setting default locale if no other locale could be determined
            setLocale(DEFAULT_LOCALE)
          }
        }
      }
    }
    initializeLocale()
  }, [])

  const setLocale = (locale) => {

    cookie.save(WECAR_LOCALE_COOKIE, locale)

    dispatch({
      type: SET_CURRENT_LOCALE,
      payload: locale
    })
  }

  const t = (key, params, forcedLanguage, defaultValue) => {
    const language = forcedLanguage || state.currentLanguage
    const components = key.split('.')
    if (!source[language]) {
      // if(!defaultValue) 
      //   throw new Error(`Language ${language} not supported`)
      return defaultValue ?? key
    }
    const section = source[language][components[0]]
    if (!section) {
      // if(!defaultValue)
      //   throw new Error(`Section ${key} not found in ${language} language`)
      return defaultValue ?? key
    }
    let value = section[components[1]]
    if (!value) {
      // if(!defaultValue)
      //   throw new Error(`Key ${key} not found in ${language} language`)
      return defaultValue ?? key
    }
    if (params) {
      for (let p in params) {
        value = value.replace(`{{${p}}}`, params[p])
      }
    }
    return value
  }

  const documentSuffixForLocale = (locale) => {
    const suffix = locale.split('-')[1]
    switch (suffix) {
      case 'FR':
        return 'fr'
      case 'NL':
        return 'nl'
      case 'BE':
        return 'en'
      case 'GB':
        return 'uk'
      default:
        return 'fr' // Default to French if no specific suffix is found
    }
  }

  const localizedDocumentUrl = (documentType) => {
    switch (documentType) {
      case 'privacy':
        return `/documents/privacy-policy-${documentSuffixForLocale(state.currentLocale)}.pdf`
      case 'application':
        return `/documents/terms-application-${documentSuffixForLocale(state.currentLocale)}.pdf`
      case 'service':
        return `/documents/terms-service-${documentSuffixForLocale(state.currentLocale)}.pdf`
      case 'cookies':
        return `/documents/cookies-policy-${documentSuffixForLocale(state.currentLocale)}.pdf`
      default:
        return ''
    }
  }

  const localizedProperty = (object, key, forcedLanguage) => {
    const language = forcedLanguage || state.currentLanguage
    if (typeof object !== 'object') return ""
    if (typeof key !== 'string') return ""
    if (typeof object[key] === 'string' || typeof object[key] === 'number' || typeof object[key] === 'boolean') return object[key]
    if (object && object[key] && object[key][language]) {
      return object[key][language]
    }
    return ""
  }

  return <LanguageContext.Provider value={{
    currentLanguage: state.currentLanguage,
    currentLocale: state.currentLocale,
    setLocale,
    localizedProperty,
    localizedDocumentUrl,
    extractArrayOfKeys,
    t
  }}>
    <>
      <Helmet>
        <title>{t('meta.title')}</title>
        <meta name="description" content={t('meta.description')} />

        <meta property="og:title" content={t('meta.title')} />
        <meta property="og:description" content={t('meta.description')} />

        <meta name="twitter:title" content={t('meta.title')} />
        <meta name="twitter:description" content="{t('meta.description')}" />
      </Helmet>
      {children}
    </>
  </LanguageContext.Provider>
}

export default LanguageState